import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "../lib/api";
import { useAuth } from "../lib/auth";
import { XPRing } from "../components/Progress";
import { Trophy, Flame, Activity, Clock, CheckCircle2, XCircle, ChevronRight } from "lucide-react";

export default function Progress() {
    const { user } = useAuth();
    const [subs, setSubs] = useState([]);

    useEffect(() => {
        client.get("/submissions/me").then((r) => setSubs(r.data));
    }, []);

    if (!user) return null;
    const xpForLevel = user.current_level * 500;
    const approved = subs.filter((s) => s.status === "approved").length;
    const pending = subs.filter((s) => s.status === "pending").length;
    const rejected = subs.filter((s) => s.status === "rejected").length;

    return (
        <div className="px-5 pt-8 pb-8">
            <span className="label-cap text-xs text-neon-green">Progress</span>
            <h1 className="font-display font-black text-4xl uppercase tracking-tight mt-1">Your Grind</h1>

            <div className="mt-6 surface-2 p-5 flex items-center gap-5">
                <XPRing value={user.xp} max={xpForLevel} size={130} color="#FF3B30" label={`/ ${xpForLevel} XP`} />
                <div className="flex-1">
                    <div className="label-cap text-xs text-neon-orange">Level</div>
                    <div className="font-display font-black text-5xl leading-none">{user.current_level}</div>
                    <div className="flex items-center gap-1 mt-3 text-neon-orange">
                        <Flame size={14} />
                        <span className="font-bold text-sm">{user.streak} day streak</span>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-4">
                <Tile icon={CheckCircle2} v={approved} l="Approved" c="#39FF14" />
                <Tile icon={Clock} v={pending} l="Pending" c="#F59E0B" />
                <Tile icon={XCircle} v={rejected} l="Rejected" c="#FF2424" />
            </div>

            <div className="mt-8">
                <div className="flex items-end justify-between">
                    <h2 className="font-display font-black text-2xl uppercase tracking-tight">Submissions</h2>
                    <Link to="/app/leaderboard" className="text-xs label-cap text-zinc-400 hover:text-white">
                        Leaderboard
                    </Link>
                </div>
                <div className="mt-3 space-y-2">
                    {subs.length === 0 ? (
                        <div className="surface-1 p-6 text-center text-zinc-400">
                            No submissions yet. Start training.
                        </div>
                    ) : (
                        subs.map((s) => (
                            <Link
                                to={`/app/submissions/${s.id}`}
                                key={s.id}
                                data-testid={`progress-sub-${s.id}`}
                                className="surface-1 p-3 flex items-center gap-3 hover:border-charcoal-400"
                            >
                                <StatusDot s={s.status} />
                                <div className="flex-1 min-w-0">
                                    <div className="font-display font-bold uppercase text-sm leading-none truncate">
                                        {s.challenge_title}
                                    </div>
                                    <div className="text-xs text-zinc-500 mt-1">
                                        {new Date(s.submitted_at).toLocaleDateString()}
                                    </div>
                                </div>
                                <ChevronRight size={16} className="text-zinc-500" />
                            </Link>
                        ))
                    )}
                </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
                <Link to="/app/badges" data-testid="progress-badges-link" className="surface-1 p-4 hover:border-neon-green">
                    <Activity className="text-neon-green mb-2" size={20} />
                    <div className="font-display font-bold uppercase">Badges</div>
                </Link>
                <Link to="/app/leaderboard" data-testid="progress-leaderboard-link" className="surface-1 p-4 hover:border-neon-orange">
                    <Trophy className="text-neon-orange mb-2" size={20} />
                    <div className="font-display font-bold uppercase">Leaderboard</div>
                </Link>
            </div>
        </div>
    );
}

function Tile({ icon: Icon, v, l, c }) {
    return (
        <div className="surface-1 p-3">
            <Icon size={16} style={{ color: c }} />
            <div className="font-display font-black text-2xl mt-1" style={{ color: c }}>{v}</div>
            <div className="label-cap text-[10px] text-zinc-500">{l}</div>
        </div>
    );
}

function StatusDot({ s }) {
    const color = s === "approved" ? "#39FF14" : s === "rejected" ? "#FF2424" : s === "resubmit" ? "#FF3B30" : "#F59E0B";
    return <div className="w-2 h-2 rounded-full shrink-0" style={{ background: color, boxShadow: `0 0 8px ${color}` }} />;
}
